import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import {
  FiArrowRight,
  FiCheck,
  FiX,
  FiUsers,
  FiDollarSign,
  FiPercent,
  FiAlertCircle,
} from "@/lib/icons";

const brokerBenefits = [
  {
    title: "Access to 70+ lenders",
    description:
      "Banks can only offer their own products. A broker compares loans from major banks, regional lenders, credit unions, and specialist lenders to find the best fit for your situation.",
    icon: <FiUsers className="size-6" />,
  },
  {
    title: "Free service",
    description:
      "Brokers are paid by lenders when your loan settles. You get expert advice, loan comparison, and application support at no cost to you.",
    icon: <FiDollarSign className="size-6" />,
  },
  {
    title: "Legal duty to act in your best interest",
    description:
      "Under Australian law, mortgage brokers must act in your best interest when providing credit assistance. Bank staff don't have this same legal obligation.",
    icon: <FiCheck className="size-6" />,
  },
  {
    title: "Saves you time",
    description:
      "We handle the research, paperwork, lender negotiations, and follow-ups. What would take you weeks, we can do in days with our established lender relationships.",
    icon: <FiPercent className="size-6" />,
  },
  {
    title: "Expert guidance for complex situations",
    description:
      "Self-employed? Multiple properties? Non-traditional income? We know which lenders suit your situation and how to present your application for the best outcome.",
    icon: <FiAlertCircle className="size-6" />,
  },
  {
    title: "Ongoing support",
    description:
      "Our relationship doesn't end at settlement. We're here for refinancing, rate reviews, and whenever your circumstances change.",
    icon: <FiArrowRight className="size-6" />,
  },
];

const comparisonData = {
  broker: [
    "Compare 70+ lenders in one search",
    "Free service (paid by lender)",
    "Legal duty to act in your best interest",
    "Handle all paperwork and follow-ups",
    "Expert advice for complex situations",
    "Access to non-bank and specialist lenders",
    "Negotiate rates on your behalf",
    "Ongoing support after settlement",
  ],
  bank: [
    "Only their own products",
    "Free service",
    "No legal best interest duty",
    "You manage your own application",
    "Generalist staff",
    "Limited to bank products only",
    "Standard advertised rates",
    "Limited post-settlement support",
  ],
};

const stats = [
  { value: "74.6%", label: "of home loans via brokers (Sept 2024)" },
  { value: "70+", label: "lenders on our panel" },
  { value: "$0", label: "cost to you for our service" },
  { value: "1,000s", label: "saved on average vs going direct" },
];

const faqs = [
  {
    question: "How do brokers get paid?",
    answer:
      "We receive a commission from the lender when your loan settles. This typically includes an upfront commission (0.5-0.7% of loan amount) and a trailing commission (0.15-0.2% annually). These commissions don't affect the interest rate or fees you pay – they come from the lender's marketing budget.",
  },
  {
    question: "Do brokers have access to all lenders?",
    answer:
      "Most brokers have access to a large panel of lenders (we have 70+), but not every lender works with brokers. Some smaller lenders are broker-only, meaning you can only access them through a broker. We'll always tell you if there's a product outside our panel that might suit you.",
  },
  {
    question: "Will using a broker affect my loan approval?",
    answer:
      "No – in fact, it often helps. Brokers know each lender's criteria and can match you with lenders most likely to approve your application. We also ensure your application is complete and well-presented, reducing delays and improving success rates.",
  },
  {
    question: "Can I negotiate a better rate myself?",
    answer:
      "You can try, but brokers often achieve better outcomes. We know the discretionary rates lenders can offer and have established relationships. We also know when to push back and which lenders are hungry for business.",
  },
  {
    question: "What if I already have a relationship with my bank?",
    answer:
      "That's fine – we can still include them in our comparison. Often, knowing you're shopping around motivates your bank to sharpen their offer. And if they are genuinely the best option, we'll tell you.",
  },
];

export function WhyUseBrokerPage() {
  return (
    <>
      <SEO
        title="Why Use a Mortgage Broker - Benefits Explained"
        description="Discover why 74.6% of Australians use mortgage brokers. Compare 70+ lenders, save time, get expert advice - all at no cost to you."
        url="/guides/why-use-a-broker"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Link
            to="/guides"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
          >
            <FiArrowRight className="size-4 mr-2 rotate-180" />
            Back to Guides
          </Link>
          <p className="section-label mb-4">Guide</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-6">
            Why Use a Mortgage Broker?
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            In the September 2024 quarter, mortgage brokers were responsible for
            74.6% of all home loan settlements in Australia – the highest on
            record. Here's why most Australians choose a broker over going
            directly to a bank.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-muted-foreground">
            <span>5 min read</span>
            <span>•</span>
            <span>Updated January 2026</span>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-serif text-3xl md:text-4xl text-primary mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <p className="section-label mb-4">The Benefits</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              Why brokers make sense
            </h2>
          </div>

          <div className="space-y-6">
            {brokerBenefits.map((benefit, index) => (
              <div
                key={benefit.title}
                className="flex gap-6 p-6 bg-white rounded-2xl border border-border/50"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center text-primary">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">
                    {index + 1}. {benefit.title}
                  </h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Comparison</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Broker vs Bank
            </h2>
            <p className="text-muted-foreground">
              See how using a broker compares to going directly to a bank.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Broker Column */}
            <div className="bg-primary text-primary-foreground rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent rounded-lg">
                  <FiUsers className="size-5 text-accent-foreground" />
                </div>
                <h3 className="font-semibold text-xl">Mortgage Broker</h3>
              </div>
              <ul className="space-y-3">
                {comparisonData.broker.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-primary-foreground/90"
                  >
                    <FiCheck className="size-5 text-primary-foreground flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Bank Column */}
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-border/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-muted rounded-lg">
                  <FiDollarSign className="size-5 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-xl">Direct to Bank</h3>
              </div>
              <ul className="space-y-3">
                {comparisonData.bank.map((item, index) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-muted-foreground"
                  >
                    {index < 3 ? (
                      <FiX className="size-5 text-red-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <span className="size-5 flex-shrink-0" />
                    )}
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Best Interest Duty */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-primary/5 border border-primary/20 rounded-3xl p-8 md:p-10">
            <h2 className="font-serif text-2xl md:text-3xl mb-4">
              The Best Interest Duty
            </h2>
            <p className="text-muted-foreground mb-6">
              Since January 2021, mortgage brokers in Australia have a legal
              obligation to act in your best interest when providing credit
              assistance. This is called the Best Interest Duty.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3">What this means for you:</h3>
                <ul className="space-y-2">
                  {[
                    "We must prioritise your interests over our own",
                    "We must recommend suitable products, not just any product",
                    "We must disclose conflicts of interest",
                    "We can be penalised for breaches",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3">Bank staff do not have this:</h3>
                <ul className="space-y-2">
                  {[
                    "Banks can recommend their own products even if not the best fit",
                    "Staff may have sales targets to meet",
                    "No legal obligation to compare with competitors",
                    "Loyalty doesn't guarantee the best deal",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <FiAlertCircle className="size-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to use a bank */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-serif text-2xl md:text-3xl mb-6">
            When might a bank be better?
          </h2>
          <p className="text-muted-foreground mb-6">
            We believe brokers are the best choice for most borrowers, but there
            are situations where going direct could make sense:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Existing customer benefits",
                description:
                  "Some banks offer loyalty discounts or package deals for customers who hold multiple products.",
              },
              {
                title: "Simple, straightforward loan",
                description:
                  "If you're a PAYG employee with a 20% deposit and clean credit, most lenders will approve you.",
              },
              {
                title: "You enjoy research",
                description:
                  "If you like comparing products yourself and have the time to do thorough research.",
              },
              {
                title: "Specific lender relationship",
                description:
                  "If you have a private banker or strong relationship with a specific lender.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-5 border border-border/50">
                <h3 className="font-medium mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-muted-foreground">
            Even in these cases, it's worth getting a broker comparison first –
            knowing the market helps you negotiate, and our service is free.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-12">
            <p className="section-label mb-4">Common Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl">
              FAQs about brokers
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold text-lg mb-3">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to compare?"
        title="Get a free loan comparison from our team"
        description="No obligation, no cost. Just expert advice tailored to your situation."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{ label: "Our Lenders", href: "/lenders" }}
      />
    </>
  );
}
